/**
 * Banka hareketinden müşteri otomatik oluştur / TC doldur.
 * - Yeni: isim+soyisim (BÜYÜK HARF) + tcKimlikNo
 * - İsimle kayıtlı (İ/I farkı dahil), TC boş: TC yaz
 * - TC ile zaten kayıtlı: dokunma
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { resolveCounterpartyTaxIdFromBankMovement, isOwnAccountTaxId } from './bankMovementTaxId';
import {
  isInternalOwnerBankTransfer,
  mentionsOwnerAccountHolder,
  resolveMovementDetail
} from './bankingMovementUi';
import { isOwnBankAccountPartyName, isOwnCompanyIban } from './ownBankAccounts';

export const normalizeMusteriDocId = (str) =>
  str
    ? foldMusteriNameKey(str)
        // fold sonrası sadece A-Z kalır; tr-TR lower 'I'→'ı' yapıp id'yi bozar
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
    : '';

export const formatMusteriUpperName = (value) => {
  const upper = String(value || '').toLocaleUpperCase('tr-TR');
  return upper.replace(/[^A-ZÇĞİÖŞÜ\s]/g, '').replace(/\s{2,}/g, ' ').trim();
};

/** İ/I, Ğ/G vb. farklarını yok sayarak eşleştirme anahtarı */
export const foldMusteriNameKey = (value) =>
  String(value || '')
    .toLocaleUpperCase('tr-TR')
    .replace(/[^A-ZÇĞİÖŞÜ\s]/g, '')
    .replace(/İ/g, 'I')
    .replace(/Ş/g, 'S')
    .replace(/Ğ/g, 'G')
    .replace(/Ü/g, 'U')
    .replace(/Ö/g, 'O')
    .replace(/Ç/g, 'C')
    .replace(/\s+/g, ' ')
    .trim();

/** Banka açıklama / ürün etiketi — gerçek kişi değil */
const BANK_NON_PERSON_PATTERNS = [
  /\bKESINTI\b/,
  /\bEKLER[I]?\b/,
  /\bALTIN\s+ALIS\b/,
  /\bALTIN\s+SATIS\b/,
  /\bKOMISYON\b/,
  /\bMASRAF\b/,
  /\bVIRMAN\b/,
  /\bBLOKE\b/,
  /\bFAIZ\b/,
  /\bBSMV\b/,
  /\bKKDF\b/,
  /\bEFT\s*UCRET/,
  /\bHAVALE\s*UCRET/,
  /\bFAST\s*UCRET/,
  /\bPOS\s+(PROVIZYON|KESINTI|TAHSIL)/,
  /\bVERGI\s+DAIRESI\b/,
  /\bMERKEZI\s+KAYIT\b/,
  /\bTAKASBANK\b/,
  /\bINTERNET\s+VERGI\b/
];

const CORPORATE_NAME_TOKENS = new Set([
  'SANAYI',
  'TICARET',
  'LIMITED',
  'ANONIM',
  'SIRKETI',
  'SIRKET',
  'LTD',
  'STI',
  'AS',
  'AŞ',
  'HOLDING',
  'KOOPERATIF',
  'VAKIF',
  'DERNEK',
  'BELEDIYE',
  'MUDURLUGU',
  'MUDURLUK'
]);

/**
 * Banka hareketindeki “gönderen/alıcı” gerçek müşteri mi, yoksa kesinti/etiket mi?
 */
export function isNonPersonBankCounterpartyName(fullName) {
  const folded = foldMusteriNameKey(fullName);
  if (!folded || folded.length < 3) return true;

  if (isOwnBankAccountPartyName(fullName)) return true;

  for (const re of BANK_NON_PERSON_PATTERNS) {
    if (re.test(folded)) return true;
  }

  const tokens = folded.split(' ').filter(Boolean);
  if (tokens.length < 2) return true;

  // Firma/ünvan parçaları (TC’siz otomatik kayıt için)
  if (tokens.some((t) => CORPORATE_NAME_TOKENS.has(t))) return true;

  // "KESINTI VE EKLERİ BE" / "SANAYI VE TICARET A" gibi kesilmiş banka satırları
  const last = tokens[tokens.length - 1];
  if (tokens.includes('VE') && last && last.length <= 2) return true;

  return false;
}

/** Otomatik müşteri için kabul edilebilir kişi adı */
export function isEligibleAutoMusteriName(fullName) {
  if (isNonPersonBankCounterpartyName(fullName)) return false;
  const folded = foldMusteriNameKey(fullName);
  const tokens = folded.split(' ').filter((t) => t.length >= 2);
  return tokens.length >= 2;
}

export const splitMusteriAdSoyad = (adSoyad) => {
  const trimmed = formatMusteriUpperName(adSoyad);
  if (!trimmed) return { isim: '', soyisim: '' };
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) {
    return { isim: trimmed, soyisim: '' };
  }
  return {
    isim: trimmed.slice(0, spaceIndex).trim(),
    soyisim: trimmed.slice(spaceIndex + 1).trim()
  };
};

const readDekDetailText = (movement) => {
  const raw = movement?.rawData || movement;
  const list = Array.isArray(raw?.enrichmentInformation)
    ? raw.enrichmentInformation
    : Array.isArray(movement?.enrichmentInformation)
      ? movement.enrichmentInformation
      : [];
  for (const info of list) {
    const code = String(info?.enrichmentCode || '').toUpperCase();
    const val = info?.enrichmentValue || info;
    if (code === 'DEK' || val?.moduleName === 'DEK') {
      const text = String(val?.detailText || '');
      if (text.trim()) return text;
    }
  }
  return '';
};

const parseAlacakliFromDek = (movement) => {
  const text = readDekDetailText(movement);
  if (!text) return '';
  const m = text.match(
    /ALACAKLI\s*:?\s*([A-ZÇĞİÖŞÜa-zçğıöşü\s.\-']{3,80}?)(?:\s{2,}|\n|ALACAKLI\s+IBAN|KOMİSYON|MASRAF|FAST\s+REF)/i
  );
  if (!m) return '';
  return formatMusteriUpperName(m[1]);
};

const resolveCounterpartyFullName = (movement) => {
  const parties = resolveMovementDetail(movement);
  // Kendi IBAN etiketleri (Kavaklı Garanti vb.) müşteri değildir
  if (movement?.direction === 'out') {
    if (isOwnCompanyIban(parties.receiverIban) || isOwnBankAccountPartyName(parties.receiverName)) {
      return '';
    }
    const fromDek = parseAlacakliFromDek(movement);
    if (fromDek) {
      if (isOwnBankAccountPartyName(fromDek)) return '';
      return fromDek;
    }
    return formatMusteriUpperName(parties.receiverName || '');
  }
  if (isOwnCompanyIban(parties.senderIban) || isOwnBankAccountPartyName(parties.senderName)) {
    return '';
  }
  return formatMusteriUpperName(parties.senderName || '');
};

export { resolveCounterpartyFullName };

/**
 * Kimlik havuzu kayıtlarını TC indeksinin beklediği şekle çevirir.
 *
 * Giden ödemelerde banka alıcının TC'sini göndermiyor; ama aynı kişi daha önce
 * bize para gönderdiyse TC'si havuzda öğrenilmiş olabilir. Bu eşleme, o bilgiyi
 * giden satırlarda da kullanılabilir kılar. Yalnızca TC/VKN taşıyan kayıtlar alınır.
 */
export function mapIdentityPoolToTaxRows(poolRows = []) {
  return (poolRows || [])
    .map((row) => {
      const tckn = String(row?.tckn || '').replace(/\D/g, '');
      const vkn = String(row?.vkn || '').replace(/\D/g, '');
      const tax = tckn.length === 11 ? tckn : vkn.length === 10 ? vkn : '';
      if (!tax) return null;

      const name =
        row?.name ||
        row?.normalizedName ||
        (Array.isArray(row?.nameHistory) && row.nameHistory.length
          ? row.nameHistory[row.nameHistory.length - 1]?.name
          : '') ||
        '';
      if (!String(name).trim()) return null;

      const parts = String(name).trim().split(/\s+/);
      return {
        id: row?.customerId || row?.id || '',
        isim: parts.slice(0, -1).join(' ') || parts[0],
        soyisim: parts.length > 1 ? parts[parts.length - 1] : '',
        tcKimlikNo: tax,
        // Müşteri kartı elle girilir ve önceliklidir; havuz kaydı ikincil kaynaktır.
        fromPool: true
      };
    })
    .filter(Boolean);
}

export function buildMusteriTaxIndex(rows = []) {
  const byNameId = new Map();
  const byUpperName = new Map();
  const byFoldedName = new Map();

  for (const row of rows) {
    const isim = formatMusteriUpperName(row?.isim || '');
    const soyisim = formatMusteriUpperName(row?.soyisim || '');
    const fullName = `${isim} ${soyisim}`.trim();
    const tckn = String(row?.tcKimlikNo || '').replace(/\D/g, '');
    const tax = tckn.length === 11 ? tckn : tckn.length === 10 ? tckn : '';
    if (!fullName) continue;

    const nameId = normalizeMusteriDocId(fullName);
    const folded = foldMusteriNameKey(fullName);
    const priority = row?.fromPool ? 1 : 2;
    const entry = {
      id: row.id || nameId,
      fullName,
      tckn: tax.length === 11 ? tax : '',
      vkn: tax.length === 10 ? tax : '',
      display: tax || '',
      label: tax.length === 11 ? 'TCKN' : tax.length === 10 ? 'VKN' : '',
      priority
    };

    /**
     * - TC'si olan kaydı TC'si olmayanla EZME.
     * - Müşteri kartı (öncelik 2) havuz kaydını (öncelik 1) geçer.
     * - AYNI öncelikte aynı isme FARKLI TC geliyorsa isim belirsizdir: hiçbir TC
     *   gösterilmez. Kolaysoft yüklemesiyle havuzda binlerce isim var; aynı isimli
     *   iki kişiden rastgele birinin TC'sini göstermek yanlış kişiyi işaret eder.
     */
    const setIfBetter = (map, key) => {
      if (!key) return;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, entry);
        return;
      }
      // Belirsiz işaretlenmiş isim ancak daha güçlü kaynakla (müşteri kartı) çözülür.
      if (prev.ambiguous) {
        if (entry.display && entry.priority > (prev.priority || 0)) map.set(key, entry);
        return;
      }
      if (prev.display && !entry.display) return;
      if (!prev.display && entry.display) {
        map.set(key, entry);
        return;
      }
      if (!entry.display) return;
      if (entry.priority > (prev.priority || 0)) {
        map.set(key, entry);
        return;
      }
      if (entry.priority < (prev.priority || 0)) return;
      if (prev.ambiguous || prev.display !== entry.display) {
        map.set(key, { ...prev, ambiguous: true, display: '', tckn: '', vkn: '', label: '' });
      }
    };

    setIfBetter(byNameId, nameId);
    setIfBetter(byUpperName, fullName);
    setIfBetter(byFoldedName, folded);
    if (row.id) setIfBetter(byNameId, String(row.id));
  }

  return { byNameId, byUpperName, byFoldedName };
}

export function resolveDisplayTaxIdForMovement(movement, musteriIndex = null) {
  const fromBank = resolveCounterpartyTaxIdFromBankMovement(movement);
  if (fromBank.display) return { ...fromBank, source: 'bank' };

  const fullName = resolveCounterpartyFullName(movement);
  if (!fullName || !musteriIndex) {
    return { tckn: '', vkn: '', display: '', label: '', source: '' };
  }

  const nameId = normalizeMusteriDocId(fullName);
  const folded = foldMusteriNameKey(fullName);
  const hit =
    (nameId && musteriIndex.byNameId?.get(nameId)) ||
    musteriIndex.byUpperName?.get(fullName) ||
    (folded && musteriIndex.byFoldedName?.get(folded)) ||
    null;

  if (hit?.display) {
    return {
      tckn: hit.tckn || '',
      vkn: hit.vkn || '',
      display: hit.display,
      label: hit.label || (hit.tckn ? 'TCKN' : 'VKN'),
      source: 'musteri'
    };
  }

  return { tckn: '', vkn: '', display: '', label: '', source: '' };
}

const loadAllMusteriler = async (sube) => {
  const snap = await getDocs(collection(db, 'subeler', sube, 'musteriler'));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
};

const findInRowsByTckn = (rows, tckn) =>
  rows.find((r) => String(r.tcKimlikNo || '').replace(/\D/g, '') === tckn) || null;

const findInRowsByName = (rows, fullName) => {
  const nameId = normalizeMusteriDocId(fullName);
  const folded = foldMusteriNameKey(fullName);
  const upper = formatMusteriUpperName(fullName);

  let hit = rows.find((r) => r.id === nameId);
  if (hit) return hit;

  hit = rows.find((r) => {
    const rn = formatMusteriUpperName(`${r.isim || ''} ${r.soyisim || ''}`.trim());
    return rn === upper || foldMusteriNameKey(rn) === folded;
  });
  return hit || null;
};

/**
 * Tek hareket — rows cache ile (TC doldurma / oluşturma).
 */
export async function upsertMusteriFromBankMovement({ sube, movement, rows = null }) {
  if (!sube || !movement) return null;
  if (movement.isMock === true) return 'skipped';
  if (isInternalOwnerBankTransfer(movement)) return 'skipped';

  const tax = resolveCounterpartyTaxIdFromBankMovement(movement);
  const tckn = tax.tckn;
  if (!tckn) return 'skipped';
  if (isOwnAccountTaxId(movement, tckn)) return 'skipped';

  const fullName = resolveCounterpartyFullName(movement);
  if (!fullName || fullName.length < 3) return 'skipped';
  if (mentionsOwnerAccountHolder(fullName)) return 'skipped';
  if (!isEligibleAutoMusteriName(fullName)) return 'skipped';

  const { isim, soyisim } = splitMusteriAdSoyad(fullName);
  if (!isim) return 'skipped';

  const nameId = normalizeMusteriDocId(`${isim} ${soyisim}`.trim());
  if (!nameId) return 'skipped';

  const list = rows || (await loadAllMusteriler(sube));

  // 1) Bu TC zaten bir müşteride var
  const byTckn = findInRowsByTckn(list, tckn);
  if (byTckn) {
    // Aynı isimde TC’siz kopya varsa ona da yaz (liste temizliği)
    const byName = findInRowsByName(list, fullName);
    if (byName && byName.id !== byTckn.id) {
      const existingTc = String(byName.tcKimlikNo || '').replace(/\D/g, '');
      if (!existingTc || existingTc.length !== 11) {
        await updateDoc(doc(db, 'subeler', sube, 'musteriler', byName.id), {
          tcKimlikNo: tckn,
          isim: formatMusteriUpperName(byName.isim || isim),
          soyisim: formatMusteriUpperName(byName.soyisim || soyisim)
        });
        byName.tcKimlikNo = tckn;
        return 'tc_updated';
      }
    }
    return 'exists';
  }

  // 2) İsimle kayıtlı (İ/I farkı dahil) → TC doldur
  const byName = findInRowsByName(list, fullName);
  if (byName) {
    const existingTc = String(byName.tcKimlikNo || '').replace(/\D/g, '');
    if (existingTc && existingTc.length === 11) return 'exists';
    await updateDoc(doc(db, 'subeler', sube, 'musteriler', byName.id), {
      tcKimlikNo: tckn,
      isim: formatMusteriUpperName(byName.isim || isim),
      soyisim: formatMusteriUpperName(byName.soyisim || soyisim)
    });
    byName.tcKimlikNo = tckn;
    return 'tc_updated';
  }

  // 3) Yeni müşteri
  const nameRef = doc(db, 'subeler', sube, 'musteriler', nameId);
  await setDoc(nameRef, {
    isim,
    soyisim,
    tcKimlikNo: tckn,
    iletisimNo: null,
    adres: null,
    bilgiler: null,
    timestamp: new Date(),
    sube,
    source: 'bank-auto'
  });
  list.push({ id: nameId, isim, soyisim, tcKimlikNo: tckn });
  return 'created';
}

export async function ensureMusteriFromBankMovement({ sube, movement, rows = null }) {
  if (!sube || !movement) return null;
  if (movement.isMock === true) return 'skipped';
  if (isInternalOwnerBankTransfer(movement)) return 'skipped';

  const tax = resolveCounterpartyTaxIdFromBankMovement(movement);
  if (tax.tckn && !isOwnAccountTaxId(movement, tax.tckn)) {
    return upsertMusteriFromBankMovement({ sube, movement, rows });
  }

  const fullName = resolveCounterpartyFullName(movement);
  if (!fullName || fullName.length < 3) return 'skipped';
  if (isOwnBankAccountPartyName(fullName)) return 'skipped';
  if (mentionsOwnerAccountHolder(fullName)) return 'skipped';
  if (!isEligibleAutoMusteriName(fullName)) return 'skipped';

  const { isim, soyisim } = splitMusteriAdSoyad(fullName);
  if (!isim) return 'skipped';

  const nameId = normalizeMusteriDocId(`${isim} ${soyisim}`.trim());
  if (!nameId) return 'skipped';

  const list = rows || (await loadAllMusteriler(sube));
  if (findInRowsByName(list, fullName)) return 'exists';

  await setDoc(doc(db, 'subeler', sube, 'musteriler', nameId), {
    isim,
    soyisim,
    tcKimlikNo: null,
    iletisimNo: null,
    adres: null,
    bilgiler: null,
    timestamp: new Date(),
    sube,
    source: 'bank-auto'
  });
  list.push({ id: nameId, isim, soyisim, tcKimlikNo: null });
  return 'created';
}

/**
 * Banka etiketinden / kendi hesap adından yanlışlıkla oluşmuş müşteri kayıtlarını sil.
 * Sadece TC’siz veya source=bank-auto olanlar.
 */
export async function cleanupJunkAutoMusteriler({ sube, rows = null }) {
  const list = rows || (await loadAllMusteriler(sube));
  let deleted = 0;

  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    const fullName = `${row.isim || ''} ${row.soyisim || ''}`.trim();
    // isNonPerson… kendi IBAN etiketlerini de junk sayar
    if (!isNonPersonBankCounterpartyName(fullName) && !isOwnBankAccountPartyName(fullName)) continue;

    const tc = String(row.tcKimlikNo || '').replace(/\D/g, '');
    const fromBank = String(row.source || '') === 'bank-auto';
    if (tc.length === 11 && !fromBank) continue;

    try {
      await deleteDoc(doc(db, 'subeler', sube, 'musteriler', row.id));
      list.splice(i, 1);
      deleted += 1;
    } catch (err) {
      console.warn('[musteri-auto] junk delete', row.id, err?.message || err);
    }
  }

  return deleted;
}

/**
 * Önce sahte etiketleri temizle; sonra TC’li / isim-only upsert.
 */
export async function upsertMusterilerFromBankMovements({ sube, movements = [] }) {
  const stats = { created: 0, tc_updated: 0, exists: 0, skipped: 0, errors: 0, deleted: 0 };
  const seenTckn = new Set();
  const seenName = new Set();
  const rows = await loadAllMusteriler(sube);

  try {
    stats.deleted = await cleanupJunkAutoMusteriler({ sube, rows });
  } catch (err) {
    console.warn('[musteri-auto] cleanup', err?.message || err);
  }

  const withTckn = [];
  const nameOnly = [];

  for (const movement of movements) {
    const tax = resolveCounterpartyTaxIdFromBankMovement(movement);
    const fullName = resolveCounterpartyFullName(movement);
    if (!isEligibleAutoMusteriName(fullName) && !tax.tckn) continue;
    const nameKey = foldMusteriNameKey(fullName) || normalizeMusteriDocId(fullName);

    if (tax.tckn) {
      if (seenTckn.has(tax.tckn)) continue;
      seenTckn.add(tax.tckn);
      withTckn.push(movement);
    } else if (nameKey) {
      if (seenName.has(nameKey)) continue;
      seenName.add(nameKey);
      nameOnly.push(movement);
    }
  }

  for (const movement of [...withTckn, ...nameOnly]) {
    try {
      const result = await ensureMusteriFromBankMovement({ sube, movement, rows });
      if (result && stats[result] !== undefined) stats[result] += 1;
    } catch (err) {
      stats.errors += 1;
      console.warn('[musteri-auto]', err?.message || err);
    }
  }

  return stats;
}
